/**
 * A boundary from a file (owner, 2026-09-28, sending a Google Earth KMZ: "draw a polygon ..."):
 * KML, KMZ (a zipped KML) or GeoJSON, to the outer ring's corners as [lat, lng] - the order the
 * boundary editor keeps them in - without the closing repeat. The first polygon in the file is taken.
 */
export async function readBoundaryFile(file: File): Promise<Array<[number, number]>> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.kmz')) return ringFromKml(await kmlFromKmz(await file.arrayBuffer()));
  const text = await file.text();
  if (name.endsWith('.kml') || text.trimStart().startsWith('<')) return ringFromKml(text);
  return ringFromGeoJson(JSON.parse(text));
}

function closeless(points: Array<[number, number]>): Array<[number, number]> {
  const [first, last] = [points[0], points[points.length - 1]];
  const closed = points.length > 3 && first && last && first[0] === last[0] && first[1] === last[1];
  const open = closed ? points.slice(0, -1) : points;
  if (open.length < 3) throw new Error('The file has no polygon of three or more corners.');
  return open;
}

/** "lng,lat[,alt] lng,lat ..." inside the first polygon's outer boundary. */
export function ringFromKml(kml: string): Array<[number, number]> {
  const doc = new DOMParser().parseFromString(kml, 'application/xml');
  const ring = doc.getElementsByTagName('Polygon')[0]?.getElementsByTagName('outerBoundaryIs')[0]
    ?.getElementsByTagName('coordinates')[0] ?? doc.getElementsByTagName('coordinates')[0];
  if (!ring?.textContent) throw new Error('No polygon found in the KML.');
  const points = ring.textContent.trim().split(/\s+/).map((pair) => {
    const [lng, lat] = pair.split(',').map(Number);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('A corner in the KML is not a number.');
    return [lat!, lng!] as [number, number];
  });
  return closeless(points);
}

function ringFromGeoJson(value: unknown): Array<[number, number]> {
  const node = value as { type?: string; features?: unknown[]; geometry?: unknown; coordinates?: unknown };
  if (node?.type === 'FeatureCollection') return ringFromGeoJson(node.features?.[0]);
  if (node?.type === 'Feature') return ringFromGeoJson(node.geometry);
  const outer = node?.type === 'Polygon' ? (node.coordinates as number[][][])[0]
    : node?.type === 'MultiPolygon' ? (node.coordinates as number[][][][])[0]?.[0] : null;
  if (!outer) throw new Error('No polygon found in the GeoJSON.');
  return closeless(outer.map(([lng, lat]) => [lat!, lng!] as [number, number]));
}

/**
 * The first .kml inside a KMZ (a zip), found through the zip's central directory - Google's KMZ files
 * leave the sizes out of the local header (they follow the data) - and inflated by the browser.
 */
async function kmlFromKmz(buffer: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) { end = i; break; }
  }
  if (end < 0) throw new Error('This KMZ is not a readable zip file.');
  const entries = view.getUint16(end + 10, true);
  let offset = view.getUint32(end + 16, true);
  for (let n = 0; n < entries && view.getUint32(offset, true) === 0x02014b50; n += 1) {
    const method = view.getUint16(offset + 10, true);
    const compressed = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const local = view.getUint32(offset + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    offset += 46 + nameLength + extraLength + commentLength;
    if (!name.toLowerCase().endsWith('.kml')) continue;
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    const data = bytes.subarray(start, start + compressed);
    if (method === 0) return new TextDecoder().decode(data);
    if (method === 8) {
      const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      return await new Response(stream).text();
    }
    throw new Error('This KMZ is compressed in a way that cannot be read here. Save it as KML instead.');
  }
  throw new Error('No KML found inside the KMZ.');
}
